/**
 * ONE status per letter, the same words on the leaver list, the interim list and the draft header
 * (David, Oct 15 2026): the furthest step the letter has reached. Only `Done` is green; red and
 * orange mean the BP has to act; everything else is plain grey text. Read from what the proxy
 * says — the request's two booleans, the signature state and the Personio filing — never decided
 * here beyond picking the words.
 */
import type { PersonioFiling, SignatureState } from '../../types/referenceLetterDraft';

/** done = green badge · alert = red badge · warn = orange badge · plain = grey text. */
export type StatusTone = 'done' | 'alert' | 'warn' | 'plain';

export interface LetterStatus {
  label: string;
  tone: StatusTone;
}

export interface LetterStatusInput {
  /** False when there is no request at all: the supervisor has sent nothing. */
  tasksSubmitted?: boolean;
  evaluationSubmitted?: boolean;
  signature?: SignatureState;
  personio?: PersonioFiling | null;
  /** Marked Done by hand: Done whatever else is true (David, Oct 15 2026). */
  done?: boolean;
}

/** Once signed, the step is the Personio filing: in Personio is the end of the road. */
function signedStatus(personio: PersonioFiling | null | undefined): LetterStatus {
  switch (personio) {
    case 'filed': return { label: 'Done', tone: 'done' };
    case 'pending': return { label: 'Signed · filing in Personio', tone: 'plain' };
    case 'failed': return { label: 'Signed · not in Personio', tone: 'alert' };
    case 'uncertain': return { label: 'Signed · check Personio', tone: 'warn' };
    // Filed by hand (sent before filing existed) or filing not set up here: the BP files it.
    default: return { label: 'Signed', tone: 'plain' };
  }
}

/**
 * Every status in the order a letter moves through them — the order of the filter chips (David,
 * Oct 15 2026: every status can be filtered for). A label missing here sorts last.
 */
export const STATUS_ORDER = [
  'Tasks and evaluation missing', 'Tasks missing', 'Evaluation missing', 'Ready to draft', 'Cancelled · ready to draft',
  'Sending…', 'Out for signature', 'May have been sent', 'Declined', 'Expired',
  'Signed · filing in Personio', 'Signed · not in Personio', 'Signed · check Personio', 'Signed', 'Done',
];

/** The letter's one status. */
export function letterStatus({ tasksSubmitted, evaluationSubmitted, signature = 'none', personio, done }: LetterStatusInput): LetterStatus {
  if (done) return { label: 'Done', tone: 'done' };
  switch (signature) {
    case 'signed': return signedStatus(personio);
    case 'sending': return { label: 'Sending…', tone: 'plain' };
    case 'out': return { label: 'Out for signature', tone: 'plain' };
    case 'uncertain': return { label: 'May have been sent', tone: 'warn' };
    case 'declined': return { label: 'Declined', tone: 'warn' };
    case 'expired': return { label: 'Expired', tone: 'warn' };
    default: break;
  }
  if (!tasksSubmitted && !evaluationSubmitted) return { label: 'Tasks and evaluation missing', tone: 'plain' };
  if (!tasksSubmitted) return { label: 'Tasks missing', tone: 'plain' };
  if (!evaluationSubmitted) return { label: 'Evaluation missing', tone: 'plain' };
  return { label: signature === 'canceled' ? 'Cancelled · ready to draft' : 'Ready to draft', tone: 'plain' };
}

/** The statuses ticked in a list's status filter (`StatusFilter`); null = all. */
export type StatusSelection = Set<string> | null;

/** Whether a row with this status is shown: nothing ticked shows everything, as in Org Planning. */
export const showsStatus = (selection: StatusSelection, label: string) => !selection?.size || selection.has(label);
